import { Redirect, type RouteComponentProps } from "react-router";

import { warrantyAddPath, warrantyAddRoute } from "utilities/flatRoutes/warranty.routes";

import { isValidId } from "commonComponents/utilities/DropDown/DropDown";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { WarrantyAddJobPickerModal } from "./WarrantyAddJobPickerModal";

/**
 * Handles `/QuickAction/WarrantyAdd/:jobId`. When the URL has no real job
 * (`jobId=0` from AllJobs/NoJobs, or any invalid value), render
 * `WarrantyAddJobPickerModal` as an overlay on the current page — quick-add is
 * global, so the popup should appear over whatever the user was looking at, the
 * same way every other quick-add modal does.
 *
 * Redirecting to `/WarrantyAdd/0` would land the user on a blank Add page because
 * the form can't render without a real `jobId`; only the modal would appear over
 * an empty background.
 *
 * We can't use `getSelectedJobIds()` to detect "no job selected" — selecting
 * AllJobs in the picker expands the sentinel into the full list of real job IDs
 * in state, so the array is non-empty. The URL param is the source of truth: the
 * quick-add link generator passes `0` when no specific job is selected.
 *
 * When a specific job is selected, redirect to the absolute warranty add page
 * (replace so the interstitial URL does not stay in history — back from the add
 * page returns to the user's previous page).
 */
export const RouteWarrantyAddQuickAction = () => (
    <RouteRelative
        path={warrantyAddPath}
        render={(routeProps: RouteComponentProps<{ jobId: string }>) => {
            const jobId = parseInt(routeProps.match.params.jobId);

            if (!isValidId(jobId)) {
                return (
                    <WarrantyAddJobPickerModal
                        history={routeProps.history}
                        location={routeProps.location}
                    />
                );
            }

            const target = warrantyAddRoute.link({ jobId });
            const pathname = typeof target === "string" ? target : target.pathname;
            return <Redirect to={pathname} />;
        }}
    />
);
